import { Icon, Text } from '@orbita-ui/core';
import {Alert, Button, MenuProps, Select, Skeleton, Space, Timeline, Typography} from 'antd';
import { CardInfoContainer, CardSectionTitle } from '../components/card.styles';
import Page from '../components/page';
import withAuthorization from '../components/hocs/withAuthorization';
import useAuthService from '../hooks/useAuthService';
import useCreateNewCardState from '../hooks/useCreateNewCardState';
import useGetBalances from '../hooks/useGetBalance';
import useGetCard from '../hooks/useGetCard';
import useGetCardTransitions from '../hooks/useGetCardTransitions';
import React, { useEffect, useState } from 'react';
import useToastContext from '../hooks/useToastContext';
import {useNavigate, useParams} from 'react-router-dom';
import { getFormatedDateWithHours, getProperDate } from '../utils/date';
import { EyeOutlined, EyeInvisibleOutlined} from '@ant-design/icons';
import useGetCardInformation from '../hooks/data/useGetCardInformation';
import {
  CARD_STATE_REASONS_TO_HUMAN_TEXT,
  CARD_STATE_TO_HUMAN_TEXT,
  HttpStatusCode,
  RealmRoles,
} from '../constants/index';
import CardsTable from '../components/cards/cards-table';
import { OwnershipLevelType } from '../interfaces/card';
import { SectionTitle } from 'components/styles';
import useGetCardHolder from 'hooks/useGetCardHolder';


export enum JCardCardState {
  CREATED = 'CREATED',
  ACTIVE = 'ACTIVE',
  SUSPENDED = 'SUSPENDED',
  BLOCKED = 'BLOCKED',
  EMBOSSED = 'EMBOSSED',
  STOLEN = 'STOLEN',
}

const STATES_REASONS_BY_STATE_VIRTUAL = {
  ACTIVE: ['CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
  SUSPENDED: ['CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
  BLOCKED: ['FRAUDULENT', 'STOLEN', 'CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
};

const STATES_REASONS_BY_STATE = {
  ACTIVE: ['CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
  SUSPENDED: ['CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
  BLOCKED: ['FRAUDULENT', 'STOLEN', 'CARD_HOLDER_INTERNAL_REASON', 'ISSUER_INTERNAL_REASON'],
};

const JCardCardStateReason = {
  CARD_HOLDER_INTERNAL_REASON: 'CARD_HOLDER_INTERNAL_REASON',
  ISSUER_INTERNAL_REASON: 'ISSUER_INTERNAL_REASON',
  FRAUDULENT: 'FRAUDULENT',
  STOLEN: 'STOLEN',
};

const HIDEN_PAN = "**** **** **** ****"

const DataDisplay = ({ title, value }) => {
  return (
    <Space>
      <Typography.Text>{`${title}: `}</Typography.Text>
      <Typography.Text type="secondary" copyable>
        {value}
      </Typography.Text>
    </Space>
  );
};

const Card = () => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const { id } = useParams<{ id: string }>();
  const { userHasRole } = useAuthService();
  const toast = useToastContext();
  const [cardInformation, setCardInformation] = useState(HIDEN_PAN);
  const { isLoading, card } = useGetCard(id);


  const {
    cardInformation: cardInformationData,
    isLoading: cardInformationIsLoading,
    isError: cardInformationIsError,
    execute: retryGetCardInformation
  } = useGetCardInformation(id);

  const {
    transitions,
    isLoading: transitionsIsLoading,
    isError: transitionsIsError,
    execute: retryTransitions,
  } = useGetCardTransitions(id);

  const { balance } = userHasRole([RealmRoles.BalanceRead]) ? useGetBalances(id) : {};
  const [isEditMode, setIsEditMode] = useState(false);
  const [isShowInformation, setShowInformation] = useState(false);
  const [cardState, setCardState] = useState(card?.state);
  const [cardStateReason, setCardStateReason] = useState(card?.state_reason);
  const { createCardState } = useCreateNewCardState(id, cardState, cardStateReason);
  const [lastChangeValue, setLastChangeValue] = useState("");
  const [reasonOptions, setReasonOptions] = useState([]);
  const {
        cardHolder,
        isLoading: isLoadingCardholder,
        getCardholder
  } = useGetCardHolder(card?.card_holder_id, false);


  const showHideInformation = () => {
    setShowInformation(!isShowInformation)
    if(isShowInformation){
      setCardInformation(HIDEN_PAN)
    }else{
      if(!cardInformationIsLoading && !cardInformationIsError){
        setCardInformation(cardInformationData.data)
      }
    }
  }

  const handleCardStateReasonChange = (value: string) => {
    setCardStateReason(value);
  };

  const handleCardStateChange = (value: string) => {
    setCardState(value);
  };

  const handleEditBtn = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    setIsEditMode(!isEditMode);
  };

  const handleSaveBtn = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    try {
      const res = await createCardState();
      if (res.status === HttpStatusCode.Ok || res.status === HttpStatusCode.NoContent) {
        toast.success('Exito', 'Se ha cambiado el estado de la tarjeta con éxito.');
        retryTransitions();
      } else {
        throw new Error('Status not created');
      }
    } catch (e: unknown) {
      console.error(e);
      toast.error('Error', 'Ocurrio un error al cambiar el estado de la tarjeta.');
    }

    setIsEditMode(!isEditMode);
  };

  const handleMenuClick: MenuProps['onClick'] = () => {
    navigate(`/${issuer}/cards/card-state-new/${id}`);
  };

  const ChangePhysicalStateBtn = withAuthorization([RealmRoles.CardsAllWrite], () => (
    <Button type="primary" onClick={handleMenuClick}>
      <Space>
        Convertir a fisica
      </Space>
    </Button>
  ));

  const getLastChange = () => {
    if(transitions?.length > 0 ) {
      const lastChange = transitions
        ?.sort((a, b) => Date.parse(b.created_time) - Date.parse(a.created_time))[0].created_time;

      return lastChange !== null ? getFormatedDateWithHours(lastChange) : getProperDate(card?.created_time);
    } else return getProperDate(card?.created_time);
  }

  useEffect(() => {
    setLastChangeValue(getLastChange());
  }, [transitions]);

  useEffect(() => {
    if (!isLoading) {
      setCardState(card?.state);
      setCardStateReason(card?.state_reason);
    }
  }, [isLoading]);

  useEffect(() => {
    const STATES_REASONS =
      card?.card_type?.toLowerCase() === 'virtual' ? STATES_REASONS_BY_STATE_VIRTUAL : STATES_REASONS_BY_STATE;
    const validReasons = STATES_REASONS[cardState as keyof typeof STATES_REASONS_BY_STATE];
    const options = validReasons?.map((reason: any) => ({
      label: CARD_STATE_REASONS_TO_HUMAN_TEXT[reason],
      value: JCardCardStateReason[reason],
    }));
    setReasonOptions(options);
  }, [cardState]);

  return (
    <Page title="Información de Tarjeta">
      <CardInfoContainer>
        <DataDisplay title="ID de la Tarjeta" value={card?.card_id} />
        <DataDisplay title="ID del Titular" value={card?.card_holder_id} />

        <DataDisplay title="Fecha Creación" value={getProperDate(card?.created_time)} />
        <DataDisplay title="Ultimo Cambio" value={lastChangeValue} />

        <DataDisplay title="Tipo de Tarjeta" value={card?.card_type} />
        <DataDisplay title="Ultimos 4 dígitos" value={card?.last_four} />

        {!isEditMode ? (
          <DataDisplay title="Estado" value={CARD_STATE_TO_HUMAN_TEXT[cardState]} />
        ) : (
          <Space>
            <Typography.Text>Estado:</Typography.Text>
            <Select
              onChange={handleCardStateChange}
              value={JCardCardState[cardState]}
              options={[
                {
                  label: 'Activa',
                  value: JCardCardState.ACTIVE,
                },
                {
                  label: 'Suspendida',
                  value: JCardCardState.SUSPENDED,
                },
                {
                  label: 'Bloqueada',
                  value: JCardCardState.BLOCKED,
                },
              ]}
              style={{ width: '14rem' }}
            />
          </Space>
        )}
        <DataDisplay title="Producto" value={card?.card_product_id} />

        {!isEditMode ? (
          <DataDisplay title="Razón del Estado" value={CARD_STATE_REASONS_TO_HUMAN_TEXT[cardStateReason]} />
        ) : (
          <Space>
            <Typography.Text>Razón del Estado:</Typography.Text>
            <Select
              onChange={handleCardStateReasonChange}
              defaultValue={cardState}
              options={reasonOptions}
              style={{ width: '14rem' }}
            />
          </Space>
        )}

        {userHasRole([RealmRoles.CardInformationRead]) && (
          <Space>
            <Typography.Text>PAN</Typography.Text>
            <Typography.Text type="secondary" copyable>
              {cardInformation}
            </Typography.Text>
            {!isShowInformation ? (
              <EyeOutlined onClick={showHideInformation}/>
            ): (
              <EyeInvisibleOutlined onClick={showHideInformation}/>
            )}
          </Space>
        )}

        <div>
          {!isEditMode ? (
            <Space>
              <Button style={{ marginRight: '10px' }} type="primary" onClick={handleEditBtn}>
                Editar
              </Button>
              {
                card?.card_type?.toLowerCase() === 'virtual' ? <ChangePhysicalStateBtn /> : ''
              }
            </Space>
          ) : (
            <Space>
              <Button danger onClick={handleEditBtn}>
                Cancelar
              </Button>

              <Button type="primary" onClick={handleSaveBtn} style={{ marginRight: '10px' }}>
                Guardar
              </Button>
            </Space>
          )}
        </div>
      </CardInfoContainer>

      {userHasRole([RealmRoles.BalanceRead]) && !balance?.data?.available && (
        <>
          <SectionTitle style={{marginBottom:'0.5em'}}>Disponible</SectionTitle>
          <Alert
            message={`Saldo disponible al dia ${getProperDate(balance?.date).concat('hs')} es de $${
              balance?.data?.available
            }`}
            type="success"
            showIcon
            style={{ marginBottom: '1rem' }}
          />
        </>
      )}

      {isLoading  ? (
        <Skeleton />
      ) : card.ownership_level != OwnershipLevelType.Additional &&  !isLoadingCardholder &&!cardHolder?.centralized_balance ? (

        <CardsTable
          searchParam={{cardParent: id, ownershipLevel: OwnershipLevelType.Additional}}
          newCardParams= {{ cardParent: id, cardholderParent: card.card_holder_id, ownershipLevel: OwnershipLevelType.Additional}}
          tableTitle="Adicionales"
        />
        ) : (
          <> </>
        )}

      <SectionTitle style={{marginBottom:'0.5em'}}>Ultimas Modificaciones</SectionTitle>
      {transitionsIsLoading && !transitionsIsError && <Skeleton />}

      {transitionsIsError && !transitionsIsLoading && (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name="WarningTriangleIcon" color="danger" size="S" />
          <Text style={{ marginLeft: '0.5rem' }}>
            No nos fue posible obtener las ultimas transiciones, por favor hace click{' '}
            <Text as="span" color="primary" onClick={() => retryTransitions()} style={{ cursor: 'pointer' }}>
              aquí
            </Text>
            para intentar nuevamente.
          </Text>
        </div>
      )}

      {!transitionsIsError && !transitionsIsLoading && transitions?.length > 0 && (
        <Timeline
          items={transitions
            ?.sort((a, b) => Date.parse(b.created_time) - Date.parse(a.created_time))
            ?.map((transition) => ({
              children: `[${getFormatedDateWithHours(transition.created_time)}] - ${
                transition?.backoffice_user?.name ?? 'Se'
              } modifico el estado de la tarjeta a ${transition.state} con la razon ${transition.reason} `,
            }))}
        />
      )}

      {!transitionsIsError && !transitionsIsLoading && transitions?.length === 0 && (
        <Alert message="Esta tarjeta no ha sufrido ninguna modificacion." type="info" showIcon />
      )}
    </Page>
  );
};

export default Card;
